import { type RefObject, useImperativeHandle } from "react";
import type { Nullable } from "@/types";

/**
 * Keeps two refs pointing at the same DOM element.
 *
 * @param sourceRef - Ref passed to a DOM element via `ref={sourceRef}`. React sets this on mount.
 * @param targetRef - Ref from another location that must read the same element.
 *
 * @example
 * ```tsx
 * function Component({ ref: sourceRef }) {
 *     const targetRef = useRef<HTMLButtonElement>(null);
 *
 *     useMergeRefs(sourceRef, targetRef);
 *
 *     const onClick = () => {
 *         // something here could call a function that needs the targetRef
 *     }
 *
 *     return <button ref={sourceRef} onClick={onClick}>...</button>;
 * }
 * ```
 */
export default function useMergeRefs<T extends HTMLElement>(
    sourceRef: RefObject<Nullable<T>>,
    targetRef: RefObject<Nullable<T>>,
) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(targetRef, () => sourceRef.current!);
}
